import { timestampDate } from "@bufbuild/protobuf/wkt";
import { ArrowRightIcon, CheckIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNotifications } from "@/hooks/useUserQueries";
import { cn } from "@/lib/utils";
import { UserNotification, UserNotification_Type } from "@/types/proto/api/v1/user_service_pb";

interface ToastData {
  id: string;
  tickerSymbol: string;
  tickerId?: number;
  sentiment: string;
  memoCount: number;
  timestamp: Date;
}

const AIAnalysisToast = () => {
  const { data: notifications = [] } = useNotifications();
  const [activeToasts, setActiveToasts] = useState<ToastData[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Check for new AI_ANALYSIS notifications
    notifications.forEach((notification: UserNotification) => {
      if (notification.type === UserNotification_Type.AI_ANALYSIS && !dismissedNotifications.has(notification.name)) {
        try {
          const content = notification.title || "{}";
          const data = JSON.parse(content);
          const toastData: ToastData = {
            id: notification.name,
            tickerSymbol: data.ticker_symbol || "Unknown",
            tickerId: data.ticker_id,
            sentiment: data.sentiment || "neutral",
            memoCount: data.memo_count || 0,
            timestamp: notification.createTime ? timestampDate(notification.createTime)! : new Date(),
          };

          // Add toast if not already active and not too old (within last 30 seconds)
          const isRecent = Date.now() - toastData.timestamp.getTime() < 30000;
          const isActive = activeToasts.some((t) => t.id === toastData.id);

          if (!isActive && isRecent) {
            setActiveToasts((prev) => [...prev, toastData]);

            // Auto-dismiss after 10 seconds
            setTimeout(() => {
              setActiveToasts((prev) => prev.filter((t) => t.id !== toastData.id));
              setDismissedNotifications((prev) => new Set([...prev, toastData.id]));
            }, 10000);
          }
        } catch (error) {
          console.error("Failed to parse notification:", error);
        }
      }
    });
  }, [notifications, activeToasts, dismissedNotifications]);

  const handleDismiss = (id: string) => {
    setActiveToasts((prev) => prev.filter((t) => t.id !== id));
    setDismissedNotifications((prev) => new Set([...prev, id]));
  };

  if (activeToasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3">
      {activeToasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            "w-96 rounded-lg shadow-lg border bg-card p-4",
            "animate-in slide-in-from-right-full fade-in",
            "duration-300 ease-in-out",
          )}
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <CheckIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">AI 分析完成: {toast.tickerSymbol}</p>
                  <p className="text-xs text-muted-foreground mb-2">基于 {toast.memoCount} 条笔记的分析已完成</p>

                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium",
                        toast.sentiment === "bullish" && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
                        toast.sentiment === "bearish" && "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
                        toast.sentiment === "neutral" && "bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400",
                      )}
                    >
                      {toast.sentiment.toUpperCase()}
                    </span>

                    {toast.tickerId && (
                      <Link
                        to={`/tickers/${toast.tickerSymbol}`}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        查看详情
                        <ArrowRightIcon className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => handleDismiss(toast.id)}
                  className="flex-shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AIAnalysisToast;
